import React, { useState } from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import {
  AuthButtonProps,
  AuthCheckboxProps,
  AuthInputProps,
  AuthLayoutProps,
  AuthScreenProps,
  CreateNewPasswordModalProps,
  InstructionsSentModalProps,
  LoginCardProps,
  PasswordStrength,
  RegisterCardProps,
  RestorePasswordModalProps,
} from "./types";
import {
  CheckIcon,
  ClearIcon,
  CloseIcon,
  EyeIcon,
  EyeOffIcon,
  LogoIcon,
} from "./icons";

// Auth screens (Figma 895:68768 "+ Auth Screens"). Dumb components — cards
// keep their own field state and report through callbacks; no router/Apollo.

// Password-strength meter levels (Figma ". passwordIndicator"): label +
// how many of the 3 segments light up in which tone.
const STRENGTH_LEVELS = {
  weak: { label: "Too short", filled: 1, tone: styles.strengthWeak },
  medium: { label: "Medium password", filled: 2, tone: styles.strengthMedium },
  strong: { label: "Strong password", filled: 3, tone: styles.strengthStrong },
} as const;

/**
 * Strength heuristic behind the Figma meter: under 8 chars is "Too short";
 * 8+ with both letters and digits (or 12+ of anything) is strong.
 * Returns null for an empty value — the meter only shows once typing starts.
 */
export const getPasswordStrength = (
  password: string,
): PasswordStrength | null => {
  if (!password) return null;
  if (password.length < 8) return "weak";
  const mixed = /[a-zA-Z]/.test(password) && /\d/.test(password);
  return mixed || password.length >= 12 ? "strong" : "medium";
};

export const AuthInput: React.FC<AuthInputProps> = ({
  label,
  type = "text",
  placeholder,
  value,
  onChange,
  onBlur,
  error = false,
  errorText,
  showToggle = false,
  clearable = false,
  largeLabel = false,
  strength = null,
  autoComplete,
  className,
}) => {
  const [revealed, setRevealed] = useState(false);
  const [focused, setFocused] = useState(false);
  const withToggle = showToggle && type === "password";
  const withClear = clearable && focused && Boolean(value);
  const level = strength ? STRENGTH_LEVELS[strength] : null;

  return (
    <label className={cs(styles.inputField, className)}>
      <span className={cs(styles.inputLabel, largeLabel && styles.inputLabelLg)}>
        {label}
      </span>
      <span className={styles.inputWrap}>
        <input
          className={cs(
            styles.inputControl,
            withToggle && styles.inputControlWithToggle,
            withClear && styles.inputControlWithClear,
            error && styles.inputControlError,
          )}
          type={withToggle && revealed ? "text" : type}
          // Always render a placeholder so :placeholder-shown drives the
          // empty/filled border color.
          placeholder={placeholder ?? " "}
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            onBlur?.();
          }}
          autoComplete={autoComplete}
        />
        {(withToggle || withClear) && (
          <span className={styles.trailingIcons}>
            {withClear && (
              <button
                type="button"
                className={styles.clearButton}
                // mousedown, not click: clears before the input loses focus.
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange?.("");
                }}
                aria-label="Clear"
              >
                <ClearIcon />
              </button>
            )}
            {withToggle && (
              <button
                type="button"
                className={styles.eyeButton}
                onClick={() => setRevealed((prev) => !prev)}
                aria-label={revealed ? "Hide password" : "Show password"}
              >
                {revealed ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            )}
          </span>
        )}
      </span>
      {level && (
        <span className={cs(styles.strengthRow, level.tone)}>
          <span className={styles.strengthLabel}>{level.label}</span>
          <span className={styles.strengthBars}>
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className={cs(
                  styles.strengthBar,
                  i < level.filled && styles.strengthBarFilled,
                )}
              />
            ))}
          </span>
        </span>
      )}
      {error && errorText && <span className={styles.errorText}>{errorText}</span>}
    </label>
  );
};

export const AuthCheckbox: React.FC<AuthCheckboxProps> = ({
  checked = false,
  onChange,
  children,
  className,
}) => (
  <label className={cs(styles.checkbox, className)}>
    <input
      className={styles.checkboxInput}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange?.(e.target.checked)}
    />
    <span className={styles.checkboxBox} aria-hidden>
      <CheckIcon />
    </span>
    {children && <span className={styles.checkboxLabel}>{children}</span>}
  </label>
);

export const AuthButton: React.FC<AuthButtonProps> = ({
  variant = "primary",
  type = "button",
  disabled = false,
  fullWidth = false,
  wide = false,
  onClick,
  children,
  className,
}) => (
  <button
    type={type}
    disabled={disabled}
    onClick={onClick}
    className={cs(
      styles.button,
      variant === "outlined" ? styles.buttonOutlined : styles.buttonPrimary,
      fullWidth && styles.buttonFull,
      wide && styles.buttonWide,
      className,
    )}
  >
    {children}
  </button>
);

/** Login form card (Figma 895:73289). */
export const LoginCard: React.FC<LoginCardProps> = ({
  title = "Welcome back!",
  subtitle = "Log in to your account",
  submitLabel = "Log in",
  submitDisabled = false,
  defaultEmail = "",
  defaultPassword = "",
  defaultRemember = true,
  emailPlaceholder = "johndoe@mail.com",
  passwordPlaceholder = "Password",
  showLegal = true,
  onSubmit,
  onForgotPassword,
  onCreateAccount,
  onPrivacyPolicy,
  onTermsOfUse,
  className,
}) => {
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState(defaultPassword);
  const [remember, setRemember] = useState(defaultRemember);

  return (
    <form
      className={cs(styles.card, className)}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit?.({ email, password, remember });
      }}
    >
      <div className={styles.cardHeading}>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.subtitle}>{subtitle}</p>
      </div>
      <div className={styles.fields}>
        <div className={styles.fieldGroup}>
          <AuthInput
            label="Email"
            type="email"
            placeholder={emailPlaceholder}
            value={email}
            onChange={setEmail}
            autoComplete="email"
          />
          <AuthInput
            label="Password"
            type="password"
            showToggle
            clearable
            placeholder={passwordPlaceholder}
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
          />
        </div>
        <div className={styles.rowBetween}>
          <AuthCheckbox checked={remember} onChange={setRemember}>
            Remember me
          </AuthCheckbox>
          <button
            type="button"
            className={styles.link}
            onClick={onForgotPassword}
          >
            Forgot password?
          </button>
        </div>
        <AuthButton type="submit" fullWidth disabled={submitDisabled}>
          {submitLabel}
        </AuthButton>
        <div className={styles.cardFooter}>
          <p className={styles.footerRow}>
            <span className={styles.muted}>Don’t have an account?</span>
            <button
              type="button"
              className={styles.link}
              onClick={onCreateAccount}
            >
              Create one
            </button>
          </p>
          {showLegal && (
            <p className={styles.legal}>
              <button
                type="button"
                className={cs(styles.link, styles.inlineLink)}
                onClick={onPrivacyPolicy}
              >
                Privacy Policy
              </button>{" "}
              and{" "}
              <button
                type="button"
                className={cs(styles.link, styles.inlineLink)}
                onClick={onTermsOfUse}
              >
                Terms of Use
              </button>
            </p>
          )}
        </div>
      </div>
    </form>
  );
};

// Production sign-up rules (mirrors pages/Registration/constants.tsx).
const NAME_PATTERN = /^[a-zA-Z\sЀ-ӿ]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Sign-up form card (Figma 895:73592). */
export const RegisterCard: React.FC<RegisterCardProps> = ({
  title = "Sign up",
  subtitle = "to boost your mobile app's success",
  submitLabel = "Register",
  defaultEmail = "",
  showNameField = false,
  defaultAgreed = true,
  validate = false,
  onSubmit,
  onGoToLogIn,
  onPrivacyPolicy,
  onTermsOfUse,
  className,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(defaultAgreed);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const errors: Record<string, string | undefined> = validate
    ? {
        name: !showNameField
          ? undefined
          : !name
            ? "Name is required"
            : name.trim().length < 2
              ? "Minimum 2 characters"
              : !NAME_PATTERN.test(name)
                ? "Name can only contain letters"
                : undefined,
        email: !email
          ? "Email is required"
          : !EMAIL_PATTERN.test(email)
            ? "Incorrect Email"
            : undefined,
        password: !password
          ? "Password is required"
          : password.length < 8
            ? "Minimum 8 characters"
            : undefined,
      }
    : {};
  const isValid = !errors.name && !errors.email && !errors.password;

  const markTouched = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  return (
    <form
      className={cs(styles.card, className)}
      onSubmit={(e) => {
        e.preventDefault();
        setTouched({ name: true, email: true, password: true });
        if (!isValid || !agreed) return;
        onSubmit?.({ ...(showNameField ? { name } : {}), email, password, agreed });
      }}
    >
      <div className={styles.cardHeading}>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.subtitle}>{subtitle}</p>
      </div>
      <div className={styles.fields}>
        <div className={styles.fieldGroup}>
          {showNameField && (
            <AuthInput
              label="Name"
              placeholder="Enter your name"
              value={name}
              onChange={setName}
              onBlur={() => markTouched("name")}
              error={Boolean(touched.name && errors.name)}
              errorText={errors.name}
              autoComplete="name"
            />
          )}
          <AuthInput
            label="Email"
            type="email"
            placeholder="johndoe@mail.com"
            value={email}
            onChange={setEmail}
            onBlur={() => markTouched("email")}
            error={Boolean(touched.email && errors.email)}
            errorText={errors.email}
            autoComplete="email"
          />
          <AuthInput
            label="Password"
            type="password"
            showToggle
            clearable
            placeholder="Password"
            value={password}
            onChange={setPassword}
            onBlur={() => markTouched("password")}
            // Same states as the create-new-password dialog: strength meter
            // while typing, error only once the field has been touched.
            strength={getPasswordStrength(password)}
            error={Boolean(touched.password && errors.password)}
            errorText={errors.password}
            autoComplete="new-password"
          />
        </div>
        <AuthCheckbox checked={agreed} onChange={setAgreed}>
          By clicking &quot;{submitLabel}&quot;, you agree to our{" "}
          <button
            type="button"
            className={cs(styles.link, styles.inlineLink)}
            onClick={onTermsOfUse}
          >
            Terms of Use
          </button>{" "}
          and{" "}
          <button
            type="button"
            className={cs(styles.link, styles.inlineLink)}
            onClick={onPrivacyPolicy}
          >
            Privacy Policy
          </button>
          .
        </AuthCheckbox>
        <AuthButton
          type="submit"
          fullWidth
          disabled={!agreed || (validate && !isValid)}
        >
          {submitLabel}
        </AuthButton>
        <div className={styles.cardFooter}>
          <p className={styles.footerRow}>
            <span className={styles.muted}>Already have an account?</span>
            <button type="button" className={styles.link} onClick={onGoToLogIn}>
              Log In
            </button>
          </p>
        </div>
      </div>
    </form>
  );
};

/** Restore-password dialog (Figma 895:74333). */
export const RestorePasswordModal: React.FC<RestorePasswordModalProps> = ({
  title = "Restore password",
  cancelLabel = "Cancel",
  sendLabel = "Send",
  validate = false,
  onSend,
  onCancel,
  onClose,
  className,
}) => {
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);

  const invalid = validate && !EMAIL_PATTERN.test(email);

  return (
    <form
      className={cs(styles.dialog, className)}
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (invalid) return;
        onSend?.(email);
      }}
    >
      <h2 className={styles.dialogTitle}>{title}</h2>
      <AuthInput
        label="Email"
        type="email"
        placeholder="Enter your email"
        value={email}
        onChange={setEmail}
        onBlur={() => setTouched(true)}
        error={touched && invalid}
        errorText="Incorrect email"
        autoComplete="email"
      />
      <div className={styles.dialogButtons}>
        <AuthButton variant="outlined" onClick={onCancel}>
          {cancelLabel}
        </AuthButton>
        <AuthButton type="submit" disabled={invalid}>
          {sendLabel}
        </AuthButton>
      </div>
      <button
        type="button"
        className={styles.close}
        onClick={onClose}
        aria-label="Close"
      >
        <CloseIcon />
      </button>
    </form>
  );
};

/** Create-a-new-password dialog (Figma 1007:15039). */
export const CreateNewPasswordModal: React.FC<CreateNewPasswordModalProps> = ({
  title = "Create a new password",
  saveLabel = "Save new password",
  validate = false,
  saving = false,
  onSave,
  onClose,
  className,
}) => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);

  const strength = getPasswordStrength(password);

  // Mirrors the production reset-password checks + the "Too short" meter.
  const passwordError = !validate
    ? undefined
    : !password
      ? "No password has been provided"
      : password.length < 8
        ? "Minimum 8 characters"
        : undefined;
  const confirmError =
    validate && !passwordError && password !== confirm
      ? "The passwords don't match"
      : undefined;
  const error = passwordError ?? confirmError;

  return (
    <form
      className={cs(styles.dialog, className)}
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (error) return;
        onSave?.(password);
      }}
    >
      <h2 className={styles.dialogTitle}>{title}</h2>
      <div className={styles.fieldGroup}>
        <AuthInput
          label="New password"
          type="password"
          showToggle
          clearable
          largeLabel
          placeholder="Enter a new password"
          value={password}
          onChange={setPassword}
          strength={strength}
          error={Boolean(touched && passwordError)}
          errorText={passwordError}
          autoComplete="new-password"
        />
        <AuthInput
          label="Confirm new password"
          type="password"
          showToggle
          clearable
          largeLabel
          placeholder="Re-enter your new password"
          value={confirm}
          onChange={setConfirm}
          onBlur={() => setTouched(true)}
          error={Boolean(touched && confirmError)}
          errorText={confirmError}
          autoComplete="new-password"
        />
      </div>
      <AuthButton type="submit" fullWidth disabled={saving}>
        {saving ? "Confirming…" : saveLabel}
      </AuthButton>
      <button
        type="button"
        className={styles.close}
        onClick={onClose}
        aria-label="Close"
      >
        <CloseIcon />
      </button>
    </form>
  );
};

/** Instructions-sent confirmation dialog (Figma 895:74409). */
export const InstructionsSentModal: React.FC<InstructionsSentModalProps> = ({
  title = "Instructions sent",
  subtitle = "Please check your email",
  buttonLabel = "Got it",
  onConfirm,
  onClose,
  className,
}) => (
  <div className={cs(styles.dialog, styles.dialogCentered, className)}>
    <div className={styles.dialogHeading}>
      <h2 className={cs(styles.dialogTitle, styles.dialogTitleLg)}>{title}</h2>
      <p className={styles.dialogSubtitle}>{subtitle}</p>
    </div>
    <AuthButton wide onClick={onConfirm}>
      {buttonLabel}
    </AuthButton>
    <button
      type="button"
      className={styles.close}
      onClick={onClose}
      aria-label="Close"
    >
      <CloseIcon />
    </button>
  </div>
);

/** Brand-blue page shell: logo on a plain brand background, optional blurred dialog. */
export const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  dialog,
  className,
}) => (
  <div className={cs(styles.screen, className)}>
    <LogoIcon className={styles.logo} />
    <div className={styles.content}>{children}</div>
    {dialog && (
      <div className={styles.dialogLayer}>
        <div className={styles.overlay} />
        {dialog}
      </div>
    )}
  </div>
);

/** One of the four auth screens, switched by `screen`. */
export const AuthScreen: React.FC<AuthScreenProps> = ({
  screen = "login",
  onLogin,
  onRegister,
  onRestoreSend,
  onSaveNewPassword,
  onForgotPassword,
  onCreateAccount,
  onGoToLogIn,
  onCancel,
  onClose,
  onConfirm,
  onPrivacyPolicy,
  onTermsOfUse,
  className,
}) => {
  // Dimmed login form behind the restore/instructions dialogs
  // (Figma frames "Password" / "Password / Success").
  const backgroundLogin = (
    <LoginCard
      submitDisabled
      emailPlaceholder="Enter your email"
      passwordPlaceholder="Enter password"
      showLegal={false}
    />
  );

  switch (screen) {
    case "register":
      return (
        <AuthLayout className={className}>
          <RegisterCard
            onSubmit={onRegister}
            onGoToLogIn={onGoToLogIn}
            onPrivacyPolicy={onPrivacyPolicy}
            onTermsOfUse={onTermsOfUse}
          />
        </AuthLayout>
      );
    case "restore-password":
      return (
        <AuthLayout
          className={className}
          dialog={
            <RestorePasswordModal
              onSend={onRestoreSend}
              onCancel={onCancel}
              onClose={onClose}
            />
          }
        >
          {backgroundLogin}
        </AuthLayout>
      );
    case "instructions-sent":
      return (
        <AuthLayout
          className={className}
          dialog={
            <InstructionsSentModal onConfirm={onConfirm} onClose={onClose} />
          }
        >
          {backgroundLogin}
        </AuthLayout>
      );
    case "create-new-password":
      return (
        <AuthLayout
          className={className}
          dialog={
            <CreateNewPasswordModal
              onSave={onSaveNewPassword}
              onClose={onClose}
            />
          }
        >
          {backgroundLogin}
        </AuthLayout>
      );
    default:
      return (
        <AuthLayout className={className}>
          <LoginCard
            defaultEmail="johndoe@mail.com"
            onSubmit={onLogin}
            onForgotPassword={onForgotPassword}
            onCreateAccount={onCreateAccount}
            onPrivacyPolicy={onPrivacyPolicy}
            onTermsOfUse={onTermsOfUse}
          />
        </AuthLayout>
      );
  }
};

export default AuthScreen;
